import React, { useState, useEffect, useCallback, useRef } from "react";

function ImageLightbox({ images, startIndex = 0, onClose }) {
  const [currentIndex, setCurrentIndex] = useState(startIndex);
  const box = useRef(null);
  const closeButton = useRef(null);

  // Move focus into the photo viewer when it opens; hand it back after.
  useEffect(() => {
    const opener = document.activeElement;
    if (closeButton.current) closeButton.current.focus();
    return () => {
      if (opener && typeof opener.focus === "function") opener.focus();
    };
  }, []);

  const goNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  }, [images.length]);

  const goPrev = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  }, [images.length]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") goNext();
      if (e.key === "ArrowLeft") goPrev();
      // Tab stays on the viewer's own buttons.
      if (e.key === "Tab" && box.current) {
        const buttons = Array.from(box.current.querySelectorAll("button"));
        if (!buttons.length) return;
        const i = buttons.indexOf(document.activeElement);
        e.preventDefault();
        const next = e.shiftKey
          ? (i <= 0 ? buttons.length - 1 : i - 1)
          : (i + 1) % buttons.length;
        buttons[next].focus();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose, goNext, goPrev]);

  return (
    <div
      ref={box}
      role="dialog"
      aria-modal="true"
      aria-label="Photo viewer"
      className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center"
      onClick={onClose}
    >
      {/* Close button */}
      <button
        ref={closeButton}
        type="button"
        aria-label="Close photo"
        onClick={onClose}
        className="absolute top-[16px] right-[16px] text-white text-[28px] font-bold hover:text-dupontGold transition-colors z-10"
      >
        &times;
      </button>

      {/* Image counter */}
      {images.length > 1 && (
        <div className="absolute top-[16px] left-1/2 -translate-x-1/2 text-white/70 text-[14px]">
          {currentIndex + 1} / {images.length}
        </div>
      )}

      {/* Previous button */}
      {images.length > 1 && (
        <button
          type="button"
          aria-label="Previous photo"
          onClick={(e) => {
            e.stopPropagation();
            goPrev();
          }}
          className="absolute left-[16px] top-1/2 -translate-y-1/2 text-white text-[36px] font-bold hover:text-dupontGold transition-colors px-[8px]"
        >
          &#8249;
        </button>
      )}

      {/* The image itself */}
      <img
        src={images[currentIndex]}
        alt={`Photo ${currentIndex + 1}`}
        className="max-h-[85vh] max-w-[90vw] object-contain rounded-[8px]"
        onClick={(e) => e.stopPropagation()}
      />

      {/* Next button */}
      {images.length > 1 && (
        <button
          type="button"
          aria-label="Next photo"
          onClick={(e) => {
            e.stopPropagation();
            goNext();
          }}
          className="absolute right-[16px] top-1/2 -translate-y-1/2 text-white text-[36px] font-bold hover:text-dupontGold transition-colors px-[8px]"
        >
          &#8250;
        </button>
      )}
    </div>
  );
}

export default ImageLightbox;
